import secureAxios from './axios/secureAxios';
import imageUploadActions from './imageUpload';

// ==================== FOLDERS ====================

async function listFolders(brandId, parentFolderId = null) {
  const params = {};
  if (parentFolderId === 'root') params.parent_folder_id = 'root';
  else if (parentFolderId) params.parent_folder_id = parentFolderId;
  const { data } = await secureAxios.get(`/brand-assets/${brandId}/folders`, { params });
  return data;
}

async function createFolder(brandId, { name, parent_folder_id = null }) {
  const { data } = await secureAxios.post(`/brand-assets/${brandId}/folders`, { name, parent_folder_id });
  return data;
}

async function updateFolder(brandId, folderId, updates) {
  const { data } = await secureAxios.patch(`/brand-assets/${brandId}/folders/${folderId}`, updates);
  return data;
}

async function deleteFolder(brandId, folderId) {
  const { data } = await secureAxios.delete(`/brand-assets/${brandId}/folders/${folderId}`);
  return data;
}

// ==================== ASSETS ====================

async function listAssets(brandId, { folder_id, asset_type, usage_type, page = 1, limit = 50 } = {}) {
  const params = { page, limit };
  if (folder_id !== undefined) params.folder_id = folder_id;
  if (asset_type) params.asset_type = asset_type;
  if (usage_type) params.usage_type = usage_type;
  const { data } = await secureAxios.get(`/brand-assets/${brandId}/assets`, { params });
  return data;
}

async function uploadAndCreateAsset(brandId, file, { asset_type, usage_type, folder_id } = {}) {
  const uploadRes = await imageUploadActions.uploadImage(file);
  if (!uploadRes?.data?.image_url && !uploadRes?.image_url) {
    throw new Error('Upload failed — no URL returned');
  }
  const url = uploadRes?.data?.image_url || uploadRes?.image_url;
  const { data } = await secureAxios.post(`/brand-assets/${brandId}/assets`, {
    url,
    name: file.name || null,
    asset_type: asset_type || 'other',
    usage_type: usage_type || 'brand_identity',
    folder_id: folder_id || null,
  });
  return data;
}

async function createAssetFromUrl(brandId, url, { asset_type, usage_type, folder_id, name } = {}) {
  const { data } = await secureAxios.post(`/brand-assets/${brandId}/assets`, {
    url,
    name: name || null,
    asset_type: asset_type || 'other',
    usage_type: usage_type || 'brand_identity',
    folder_id: folder_id || null,
  });
  return data;
}

async function updateAsset(brandId, assetId, updates) {
  const { data } = await secureAxios.patch(`/brand-assets/${brandId}/assets/${assetId}`, updates);
  return data;
}

async function deleteAsset(brandId, assetId) {
  const { data } = await secureAxios.delete(`/brand-assets/${brandId}/assets/${assetId}`);
  return data;
}

async function recaptionAsset(brandId, assetId) {
  const { data } = await secureAxios.post(`/brand-assets/${brandId}/assets/${assetId}/caption`);
  return data;
}

async function getFolderCounts(brandId) {
  const { data } = await secureAxios.get(`/brand-assets/${brandId}/folders/counts`);
  return data;
}

// ==================== MOODBOARDS ====================

async function listMoodboards(brandId) {
  const { data } = await secureAxios.get(`/brand-moodboards/${brandId}/moodboards`);
  return data;
}

async function getMoodboard(brandId, moodboardId) {
  const { data } = await secureAxios.get(`/brand-moodboards/${brandId}/moodboards/${moodboardId}`);
  return data;
}

async function createMoodboard(brandId, { title, description }) {
  const { data } = await secureAxios.post(`/brand-moodboards/${brandId}/moodboards`, { title, description });
  return data;
}

async function updateMoodboard(brandId, moodboardId, updates) {
  const { data } = await secureAxios.patch(`/brand-moodboards/${brandId}/moodboards/${moodboardId}`, updates);
  return data;
}

async function deleteMoodboard(brandId, moodboardId) {
  const { data } = await secureAxios.delete(`/brand-moodboards/${brandId}/moodboards/${moodboardId}`);
  return data;
}

// ==================== MOODBOARD ITEMS ====================

async function addMoodboardItemFromUrl(brandId, moodboardId, url, name = null) {
  const { data } = await secureAxios.post(`/brand-moodboards/${brandId}/moodboards/${moodboardId}/items`, { url, name });
  return data;
}

async function uploadAndAddMoodboardItem(brandId, moodboardId, file) {
  const uploadRes = await imageUploadActions.uploadImage(file);
  if (!uploadRes?.data?.image_url && !uploadRes?.image_url) {
    throw new Error('Upload failed — no URL returned');
  }
  const url = uploadRes?.data?.image_url || uploadRes?.image_url;
  const { data } = await secureAxios.post(`/brand-moodboards/${brandId}/moodboards/${moodboardId}/items`, {
    url,
    name: file.name || null,
  });
  return data;
}

async function removeMoodboardItem(brandId, moodboardId, itemId) {
  const { data } = await secureAxios.delete(
    `/brand-moodboards/${brandId}/moodboards/${moodboardId}/items/${itemId}`
  );
  return data;
}

export const brandAssetActions = {
  listFolders,
  createFolder,
  updateFolder,
  deleteFolder,
  getFolderCounts,
  listAssets,
  uploadAndCreateAsset,
  createAssetFromUrl,
  updateAsset,
  deleteAsset,
  recaptionAsset,
  listMoodboards,
  getMoodboard,
  createMoodboard,
  updateMoodboard,
  deleteMoodboard,
  addMoodboardItemFromUrl,
  uploadAndAddMoodboardItem,
  removeMoodboardItem,
};

export default brandAssetActions;
